import {
  EVUA_AC_CONNECTORS,
  EVUA_FAST_CONNECTORS,
  EVUA_MIXED_HUB_AC_POWER_KW,
  MAP_FILTER_SLOW_MAX_KW,
  getEvuaPriceUahPerKwh,
  isEvuaSlowMixedHub,
} from './evuaMapFilters';
import { HOST, PORT } from '../../environment';

export const EVUA_CONNECTOR_LABELS = {
  ccs1: 'CCS1',
  ccs2: 'CCS2',
  chd: 'CHAdeMO',
  gbt: 'GB/T',
  nacs: 'NACS',
  gbtadapter: 'GB/T Adapter',
  type1: 'Type 1',
  type2: 'Type 2',
  type2socket: 'Type 2 socket',
  gbtac: 'GB/T AC',
};

/** Canonical display names for EV UA billing operators. */
const EVUA_OPERATOR_LABELS = {
  toka: 'Toka',
  ecofactor: 'EcoFactor',
  'go to-u': 'Go To-U',
  'goto-u': 'Go To-U',
  'go to u': 'Go To-U',
  go2you: 'Go To-U',
  g2y: 'Go To-U',
  'eva chargers': 'EVA Chargers',
  flash: 'FLASH',
  yasno: 'YASNO',
  ugv: 'UGV',
  cloudpay: 'CloudPay',
  '220-km.com': '220 км',
  '220km': '220 км',
  '220-km': '220 км',
};

const CACHE_STORAGE_KEY = 'evuaStationDetailsCache.v3';
const CACHE_TTL_MS = 24 * 60 * 60 * 1000;
/** Allow slow EV UA pages to resolve operator / name. */
export const EVUA_PAGE_META_FETCH_TIMEOUT_MS = 20000;

export function formatEvuaConnectorLabel(code) {
  if (code == null || code === '') return null;
  const key = String(code).toLowerCase();
  return EVUA_CONNECTOR_LABELS[key] || String(code).toUpperCase();
}

export function formatEvuaConnectors(connectors) {
  if (!Array.isArray(connectors) || connectors.length === 0) {
    return [];
  }
  return connectors.map(formatEvuaConnectorLabel).filter(Boolean);
}

/** Decode HTML entities and strip punctuation; keep letters, digits, spaces, dots, hyphens. */
export function cleanEvuaDisplayText(raw) {
  if (raw == null) return null;
  let text = String(raw);
  text = text.replace(/&#(\d+);/g, (_, code) => String.fromCharCode(Number(code)));
  text = text.replace(/&#x([0-9a-f]+);/gi, (_, hex) => String.fromCharCode(parseInt(hex, 16)));
  text = text
    .replace(/&quot;/gi, '"')
    .replace(/&apos;/gi, "'")
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&nbsp;/gi, ' ');
  text = text
    .replace(/[^\p{L}\p{N}\s.-]+/gu, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  return text || null;
}

/** Normalize operator from EV UA billing CTA (Toka, EcoFactor, Go To-U, …). */
export function formatEvuaOperatorLabel(raw) {
  if (raw == null) return null;
  const trimmed = cleanEvuaDisplayText(raw);
  if (!trimmed) return null;
  const key = trimmed.toLowerCase().replace(/\s+/g, ' ');
  if (EVUA_OPERATOR_LABELS[key]) {
    return EVUA_OPERATOR_LABELS[key];
  }
  // Keep short brand-like strings as-is.
  if (/^без білінгу$/i.test(key) || /^без биллинга$/i.test(key) || key === 'no billing') {
    return null;
  }
  if (/^[A-Z0-9 .-]+$/.test(trimmed) && trimmed.length <= 24) {
    return trimmed;
  }
  return trimmed;
}

export function formatEvuaPriceLabel(portOrDetails, { acdc = 'all' } = {}) {
  const minp = Number(portOrDetails?.minp);
  const maxp = Number(portOrDetails?.maxp);
  const hasMin = Number.isFinite(minp) && minp > 0;
  const hasMax = Number.isFinite(maxp) && maxp > 0;
  if (acdc === 'ac' && hasMin) {
    return formatPriceNumber(minp);
  }
  if (acdc === 'dc' && hasMax) {
    return formatPriceNumber(maxp);
  }
  const con = Array.isArray(portOrDetails?.con) ? portOrDetails.con : [];
  const hasAc = con.some(code => EVUA_AC_CONNECTORS.has(code));
  const hasDc = con.some(code => EVUA_FAST_CONNECTORS.has(code));
  // minp is AC (type1/type2/gbtac). Mixed hubs and DC pins that inherited that range show DC maxp only.
  if (hasMax && hasMin && minp !== maxp && (hasAc || hasDc)) {
    return formatPriceNumber(maxp);
  }
  if (hasMin && hasMax && minp !== maxp) {
    return `${formatPriceNumber(minp)}–${formatPriceNumber(maxp)}`;
  }
  const single = getEvuaPriceUahPerKwh(portOrDetails, { acdc });
  if (single == null) return null;
  return formatPriceNumber(single);
}

function slowPowerKwForLabel(portOrDetails) {
  const minpow = Number(portOrDetails?.minpow);
  if (Number.isFinite(minpow) && minpow > 0 && minpow < MAP_FILTER_SLOW_MAX_KW) {
    return minpow;
  }
  return EVUA_MIXED_HUB_AC_POWER_KW;
}

/** Rated power label from EV UA {@code minpow}/{@code maxpow} (kW). Slow is always below 30 kW. */
export function formatEvuaPowerLabel(portOrDetails, { acdc = 'all' } = {}) {
  if (acdc === 'ac' && isEvuaSlowMixedHub(portOrDetails)) {
    return formatPowerNumber(slowPowerKwForLabel(portOrDetails));
  }
  const minpow = Number(portOrDetails?.minpow);
  const maxpow = Number(portOrDetails?.maxpow);
  const hasMin = Number.isFinite(minpow) && minpow > 0;
  const hasMax = Number.isFinite(maxpow) && maxpow > 0;
  if (acdc === 'ac' && ((hasMin && minpow >= MAP_FILTER_SLOW_MAX_KW) || (hasMax && maxpow >= MAP_FILTER_SLOW_MAX_KW))) {
    return formatPowerNumber(slowPowerKwForLabel(portOrDetails));
  }
  if (hasMin && hasMax && minpow !== maxpow) {
    return `${formatPowerNumber(minpow)}–${formatPowerNumber(maxpow)}`;
  }
  if (hasMax) return formatPowerNumber(maxpow);
  if (hasMin) return formatPowerNumber(minpow);
  return null;
}

function formatPriceNumber(value) {
  return Number.isInteger(value) ? String(value) : String(Math.round(value * 100) / 100);
}

function formatPowerNumber(value) {
  return Number.isInteger(value) ? String(value) : String(Math.round(value * 10) / 10);
}

/**
 * Lazy-fetch provider/operator + vendor + name from our API (parses EV UA station page).
 * Falls back to CRA `/dev/evua-station-meta` scrape when remote BE has no meta endpoint yet.
 * @returns {{ operator: string|null, vendor: string|null, name: string|null }}
 */
export async function fetchEvuaStationPageMeta(
  stationKey,
  { timeoutMs = EVUA_PAGE_META_FETCH_TIMEOUT_MS, signal } = {}
) {
  const empty = { operator: null, vendor: null, name: null };
  if (stationKey == null || stationKey === '') {
    return empty;
  }
  const controller = new AbortController();
  const onAbort = () => controller.abort();
  if (signal) {
    if (signal.aborted) {
      return empty;
    }
    signal.addEventListener('abort', onAbort, { once: true });
  }
  const timer = setTimeout(() => controller.abort(), timeoutMs);

  const readJson = async url => {
    try {
      const response = await fetch(url, {
        signal: controller.signal,
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) {
        return null;
      }
      const contentType = response.headers.get('content-type') || '';
      if (!contentType.includes('application/json')) {
        return null;
      }
      return await response.json();
    } catch (_e) {
      return null;
    }
  };

  try {
    const key = encodeURIComponent(stationKey);
    const backendBase = HOST && PORT ? `${HOST}:${PORT}` : '';
    let data = backendBase ? await readJson(`${backendBase}/api/roaming/evua/stations/${key}/meta`) : null;

    // Prod BE may not have /meta yet; local CRA proxy scrapes evua.site directly.
    if (!data?.operator || !data?.vendor) {
      const scraped = await readJson(`/dev/evua-station-meta/${key}`);
      if (scraped) {
        data = {
          operator: data?.operator || scraped.operator,
          vendor: data?.vendor || scraped.vendor,
          name: data?.name || scraped.name,
        };
      }
    }

    if (!data) {
      return empty;
    }

    return {
      operator: formatEvuaOperatorLabel(data?.operator),
      vendor: cleanEvuaDisplayText(data?.vendor),
      name: cleanEvuaDisplayText(data?.name),
    };
  } catch (_e) {
    return empty;
  } finally {
    clearTimeout(timer);
    if (signal) {
      signal.removeEventListener('abort', onAbort);
    }
  }
}

export function normalizeEvuaStationDetails(port, { acdc = 'all' } = {}) {
  if (!port || typeof port !== 'object') {
    return null;
  }
  const key = port.key != null ? port.key : port.number;
  if (key == null || key === '') {
    return null;
  }
  const latitude = port.latitude ?? port.lat;
  const longitude = port.longitude ?? port.lon;
  const mixedSlow = acdc === 'ac' && isEvuaSlowMixedHub(port);
  let con = Array.isArray(port.con) ? [...port.con] : [];
  if (mixedSlow) {
    con = con.filter(code => EVUA_AC_CONNECTORS.has(code));
    if (con.length === 0) {
      con = ['type1', 'type2'];
    }
  }
  const connectors = formatEvuaConnectors(con);
  const price = formatEvuaPriceLabel(port, { acdc });
  const power = formatEvuaPowerLabel(port, { acdc });
  const minp = Number(port.minp);
  const maxp = mixedSlow && Number.isFinite(minp) && minp > 0 ? minp : Number(port.maxp);
  const slowKw = mixedSlow ? slowPowerKwForLabel(port) : null;
  const minpow = mixedSlow ? slowKw : Number(port.minpow);
  const maxpow = mixedSlow ? slowKw : Number(port.maxpow);
  const operator = formatEvuaOperatorLabel(port.operator);
  const vendor = cleanEvuaDisplayText(port.vendor);

  return {
    key,
    name: cleanEvuaDisplayText(port.name),
    operator,
    vendor,
    latitude: Number.isFinite(Number(latitude)) ? Number(latitude) : null,
    longitude: Number.isFinite(Number(longitude)) ? Number(longitude) : null,
    connectors,
    con,
    minp: Number.isFinite(minp) ? minp : null,
    maxp: Number.isFinite(maxp) ? maxp : null,
    minpow: Number.isFinite(minpow) && minpow > 0 ? minpow : null,
    maxpow: Number.isFinite(maxpow) && maxpow > 0 ? maxpow : null,
    price,
    power,
  };
}

function readCacheStore() {
  try {
    const raw = localStorage.getItem(CACHE_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch (_e) {
    return {};
  }
}

function writeCacheStore(store) {
  try {
    localStorage.setItem(CACHE_STORAGE_KEY, JSON.stringify(store));
  } catch (_e) {
    /* quota / private mode */
  }
}

export function getCachedEvuaStationDetails(stationKey) {
  if (stationKey == null || stationKey === '') return null;
  const store = readCacheStore();
  const entry = store[String(stationKey)];
  if (!entry || typeof entry !== 'object') return null;
  if (!Number.isFinite(entry.savedAt) || Date.now() - entry.savedAt > CACHE_TTL_MS) {
    delete store[String(stationKey)];
    writeCacheStore(store);
    return null;
  }
  return entry.details && typeof entry.details === 'object' ? entry.details : null;
}

export function setCachedEvuaStationDetails(details) {
  if (!details?.key && details?.key !== 0) return;
  const store = readCacheStore();
  const now = Date.now();
  Object.keys(store).forEach(key => {
    const entry = store[key];
    if (!entry || !Number.isFinite(entry.savedAt) || now - entry.savedAt > CACHE_TTL_MS) {
      delete store[key];
    }
  });
  // Drop legacy photo fields from cache entries.
  const safeDetails = { ...details };
  delete safeDetails.photoUrls;
  store[String(details.key)] = { savedAt: now, details: safeDetails };
  writeCacheStore(store);
}

/**
 * Lazy-load station details on user click: prefer fresh port fields, fill gaps from 1-day cache,
 * and resolve provider / vendor / name via API when missing.
 */
export async function loadEvuaStationDetails(port, { signal, acdc = 'all' } = {}) {
  const key = port?.key != null ? port.key : port?.number;
  const cached = getCachedEvuaStationDetails(key);
  const fresh = normalizeEvuaStationDetails(port, { acdc });

  if (!fresh && !cached) {
    return { details: null, fromCache: false };
  }

  let pageOperator = formatEvuaOperatorLabel(fresh?.operator ?? cached?.operator);
  let pageVendor = cleanEvuaDisplayText(fresh?.vendor ?? cached?.vendor);
  let pageName = cleanEvuaDisplayText(fresh?.name ?? cached?.name);
  const haveListMeta = Boolean(pageOperator && pageName);
  const needPageMeta =
    !haveListMeta &&
    (!pageOperator || !pageName || !(cached && Object.prototype.hasOwnProperty.call(cached, 'vendor')));

  if (needPageMeta) {
    const pageMeta = await fetchEvuaStationPageMeta(key, { signal });
    if (pageMeta.operator) {
      pageOperator = pageMeta.operator;
    }
    if (pageMeta.vendor) {
      pageVendor = pageMeta.vendor;
    }
    if (pageMeta.name) {
      pageName = pageMeta.name;
    }
  }

  const details = {
    ...(cached || {}),
    ...(fresh || {}),
    connectors: fresh?.connectors?.length > 0 ? fresh.connectors : cached?.connectors?.length ? cached.connectors : [],
    con: fresh?.con?.length > 0 ? fresh.con : cached?.con?.length ? cached.con : [],
    price: fresh?.price ?? cached?.price ?? null,
    power: fresh?.power ?? cached?.power ?? null,
    minp: fresh?.minp ?? cached?.minp ?? null,
    maxp: fresh?.maxp ?? cached?.maxp ?? null,
    minpow: fresh?.minpow ?? cached?.minpow ?? null,
    maxpow: fresh?.maxpow ?? cached?.maxpow ?? null,
    name: pageName,
    operator: pageOperator,
    // Always persist vendor key (null when absent) so we do not re-hit meta forever.
    vendor: pageVendor,
  };
  delete details.photoUrls;

  setCachedEvuaStationDetails(details);
  const hasFreshFields = Boolean(
    port &&
    (Array.isArray(port.con) ||
      port.minp != null ||
      port.maxp != null ||
      port.minpow != null ||
      port.maxpow != null ||
      port.name ||
      port.operator ||
      port.vendor ||
      port.lat != null ||
      port.latitude != null)
  );
  return { details, fromCache: Boolean(cached) && !hasFreshFields };
}
